import { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { api, subRoleLabel } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Target, Send, CheckCircle2, XCircle, Flame } from "lucide-react";
import { toast } from "sonner";

const KPI_ROW = [
  { key: "calls", label: "Calls" },
  { key: "leads", label: "Leads" },
  { key: "appointments", label: "Appts" },
  { key: "offers", label: "Offers" },
  { key: "contracts", label: "Contracts" },
];

const SideStats = ({ label, bundle, tid }) => (
  <div data-testid={tid} className="hairline bg-[#0F0F0F] p-6">
    <div className="flex items-center gap-3 mb-4">
      {bundle.user?.picture ? (
        <img src={bundle.user.picture} className="w-11 h-11 hairline object-cover" alt="" />
      ) : (
        <div className="w-11 h-11 bg-[#1a1a1a] hairline flex items-center justify-center font-bold">
          {bundle.user?.name?.[0]}
        </div>
      )}
      <div>
        <div className="text-[10px] uppercase tracking-widest text-[#39FF14]">{label}</div>
        <div className="font-semibold">{bundle.user?.name}</div>
        <div className="text-[10px] uppercase tracking-widest text-neutral-500">
          {subRoleLabel(bundle.user?.sub_role)}
        </div>
      </div>
    </div>
    <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">Week totals</div>
    <div className="grid grid-cols-5 gap-2 mb-4">
      {KPI_ROW.map((f) => (
        <div key={f.key} className="hairline bg-[#0A0A0A] p-2 text-center">
          <div className="font-display text-lg font-bold tabular">{bundle.week?.[f.key] || 0}</div>
          <div className="text-[9px] uppercase tracking-widest text-neutral-500">{f.label}</div>
        </div>
      ))}
    </div>
    <div className="hairline bg-[#0A0A0A] p-3 flex items-baseline justify-between">
      <span className="text-[10px] uppercase tracking-widest text-neutral-500">Revenue</span>
      <span className="font-display text-2xl font-bold text-[#39FF14] tabular">
        ${Number(bundle.week?.revenue || 0).toLocaleString()}
      </span>
    </div>
  </div>
);

export default function PartnerRoom() {
  const { id } = useParams();
  const { user } = useAuth();
  const [dash, setDash] = useState(null);
  const [chat, setChat] = useState([]);
  const [msg, setMsg] = useState("");
  const [goalEdit, setGoalEdit] = useState("");
  const [checkins, setCheckins] = useState([]);
  const [checkinNote, setCheckinNote] = useState("");
  const chatEnd = useRef(null);

  const load = async () => {
    const [d, c, k] = await Promise.all([
      api.get(`/partners/${id}/dashboard`),
      api.get(`/partners/${id}/chat`),
      api.get(`/partners/${id}/checkins`),
    ]);
    setDash(d.data);
    setChat(c.data);
    setCheckins(k.data);
    setGoalEdit(d.data.partnership?.shared_goal || "");
  };

  useEffect(() => { load(); const t = setInterval(load, 10000); return () => clearInterval(t); }, [id]);
  useEffect(() => { chatEnd.current?.scrollIntoView({ behavior: "smooth" }); }, [chat.length]);

  const sendMsg = async () => {
    if (!msg.trim()) return;
    try {
      await api.post(`/partners/${id}/chat`, { text: msg });
      setMsg("");
      load();
    } catch { toast.error("Failed to send"); }
  };

  const saveGoal = async () => {
    await api.post(`/partners/${id}/goal`, { shared_goal: goalEdit });
    toast.success("Shared goal saved");
    load();
  };

  const checkIn = async (onTrack) => {
    await api.post(`/partners/${id}/checkin`, { on_track: onTrack, note: checkinNote });
    setCheckinNote("");
    toast.success(onTrack ? "Locked in ✓" : "Marked off track");
    load();
  };

  if (!dash) return <div className="p-10 text-neutral-500">Loading…</div>;

  const todayCheckins = checkins.filter((c) => c.checkin_date === new Date().toISOString().slice(0, 10));

  return (
    <div className="p-8 md:p-10 max-w-[1400px]">
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-widest text-[#39FF14] mb-2">// Partner room</div>
        <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight">
          You <span className="text-neutral-500">+</span> {dash.partner.user.name}
        </h1>
      </div>

      {/* Shared goal */}
      <div className="hairline bg-[#0F0F0F] p-6 mb-3">
        <div className="flex items-center gap-2 mb-3">
          <Target size={16} className="text-[#39FF14]" />
          <h2 className="font-display text-sm font-bold uppercase tracking-widest">Shared goal</h2>
        </div>
        <div className="flex gap-2">
          <Input
            data-testid="shared-goal-input"
            value={goalEdit}
            onChange={(e) => setGoalEdit(e.target.value)}
            placeholder="e.g. 500 calls, 10 contracts, $30K revenue this week"
            className="rounded-none bg-[#0A0A0A] hairline focus-visible:ring-1 focus-visible:ring-[#39FF14] focus-visible:border-[#39FF14]"
          />
          <Button
            data-testid="save-goal-btn"
            onClick={saveGoal}
            className="rounded-none bg-[#39FF14] text-black hover:bg-white h-10 text-xs uppercase tracking-widest px-4"
          >
            Set
          </Button>
        </div>
      </div>

      {/* Side-by-side dashboards */}
      <div className="grid md:grid-cols-2 gap-3 mb-3">
        <SideStats tid="side-you" label="You" bundle={dash.you} />
        <SideStats tid="side-partner" label="Partner" bundle={dash.partner} />
      </div>

      <div className="grid lg:grid-cols-3 gap-3">
        {/* Check-ins */}
        <div className="hairline bg-[#0F0F0F] p-6">
          <div className="flex items-center gap-2 mb-4">
            <Flame size={16} className="text-[#39FF14]" />
            <h2 className="font-display text-sm font-bold uppercase tracking-widest">Daily check-in</h2>
          </div>
          <Textarea
            data-testid="checkin-note"
            value={checkinNote}
            onChange={(e) => setCheckinNote(e.target.value)}
            placeholder="What did you crush today? What's blocking?"
            className="rounded-none bg-[#0A0A0A] hairline mb-3 min-h-[80px]"
          />
          <div className="grid grid-cols-2 gap-2 mb-4">
            <Button
              data-testid="checkin-ontrack"
              onClick={() => checkIn(true)}
              className="rounded-none bg-[#39FF14] text-black hover:bg-white h-10 text-xs uppercase tracking-widest"
            >
              <CheckCircle2 size={14} className="mr-1" /> On track
            </Button>
            <Button
              data-testid="checkin-off"
              onClick={() => checkIn(false)}
              variant="outline"
              className="rounded-none hairline bg-transparent h-10 text-xs uppercase tracking-widest hover:text-red-400"
            >
              <XCircle size={14} className="mr-1" /> Off
            </Button>
          </div>
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">Recent</div>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {checkins.length === 0 && <div className="text-xs text-neutral-500">No check-ins yet.</div>}
            {checkins.map((c) => (
              <div key={c.checkin_id} className="hairline bg-[#0A0A0A] p-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] uppercase tracking-widest text-neutral-500">
                    {c.user_id === user.user_id ? "You" : dash.partner.user.name} · {c.checkin_date}
                  </span>
                  {c.on_track ? (
                    <span className="text-[10px] uppercase tracking-widest text-[#39FF14]">On track</span>
                  ) : (
                    <span className="text-[10px] uppercase tracking-widest text-red-400">Off</span>
                  )}
                </div>
                {c.note && <div className="text-xs text-neutral-300">{c.note}</div>}
              </div>
            ))}
          </div>
        </div>

        {/* Chat */}
        <div className="lg:col-span-2 hairline bg-[#0F0F0F] p-6 flex flex-col" style={{ minHeight: 460 }}>
          <div className="flex items-center gap-2 mb-4">
            <Send size={16} className="text-[#39FF14]" />
            <h2 className="font-display text-sm font-bold uppercase tracking-widest">Chat</h2>
          </div>
          <div data-testid="chat-messages" className="flex-1 overflow-y-auto space-y-2 mb-3 pr-2">
            {chat.length === 0 && <div className="text-xs text-neutral-500">Send the first message.</div>}
            {chat.map((m) => {
              const mine = m.from_user_id === user.user_id;
              return (
                <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[70%] px-3 py-2 hairline ${
                      mine ? "bg-[#39FF14] text-black border-[#39FF14]" : "bg-[#0A0A0A]"
                    }`}
                  >
                    <div className="text-sm whitespace-pre-wrap break-words">{m.text}</div>
                    <div className={`text-[9px] uppercase tracking-widest mt-1 ${mine ? "text-black/60" : "text-neutral-500"}`}>
                      {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                    </div>
                  </div>
                </div>
              );
            })}
            <div ref={chatEnd} />
          </div>
          <div className="flex gap-2">
            <Input
              data-testid="chat-input"
              value={msg}
              onChange={(e) => setMsg(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && sendMsg()}
              placeholder="Message your partner…"
              className="rounded-none bg-[#0A0A0A] hairline focus-visible:ring-1 focus-visible:ring-[#39FF14] focus-visible:border-[#39FF14]"
            />
            <Button
              data-testid="chat-send"
              onClick={sendMsg}
              className="rounded-none bg-[#39FF14] text-black hover:bg-white h-10 text-xs uppercase tracking-widest px-4"
            >
              <Send size={14} />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
